import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';

// Input primitive. Default + mono variants, inline error state.
// See preview/inputs.html.

export type InputVariant = 'sans' | 'mono';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  variant?: InputVariant;
  errorText?: string;
  helperText?: string;
  label?: string;
  leadingAddon?: ReactNode;
  trailingAddon?: ReactNode;
};

const fieldBase =
  'flex items-stretch rounded-input border bg-card transition-colors';

const fieldDefault = 'border-border-strong focus-within:border-primary focus-within:border-2';
const fieldError = 'border-danger bg-danger-bg';
const fieldDisabled = 'opacity-60 bg-muted';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    variant = 'sans',
    errorText,
    helperText,
    label,
    leadingAddon,
    trailingAddon,
    className = '',
    id,
    disabled,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const helperId = `${inputId}-helper`;

  const hasError = !!errorText;
  const fieldClass = [
    fieldBase,
    hasError ? fieldError : disabled ? fieldDisabled : fieldDefault,
  ].join(' ');

  const inputFontClass = variant === 'mono' ? 'font-mono' : 'font-sans';

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className="text-xs font-semibold text-foreground">
          {label}
        </label>
      ) : null}
      <div className={fieldClass}>
        {leadingAddon ? (
          <span className="flex items-center px-2.5 text-xs text-foreground-mute border-r border-border">
            {leadingAddon}
          </span>
        ) : null}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={errorText || helperText ? helperId : undefined}
          className={`flex-1 px-3 py-2 bg-transparent text-sm text-foreground placeholder:text-foreground-soft outline-none ${inputFontClass} ${className}`}
        />
        {trailingAddon ? (
          <span className="flex items-center px-2.5 text-xs text-foreground-mute border-l border-border">
            {trailingAddon}
          </span>
        ) : null}
      </div>
      {errorText ? (
        <p id={helperId} className="text-xs text-danger">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helperId} className="text-xs text-foreground-mute">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
